/**
 * OAuthConsentPage — Landlord / Manager
 *
 * Route: /oauth/consent?req=<authorizationId>
 *
 * Loads consent details from the backend and, on approve/deny, redirects the
 * browser to the redirect_uri the backend returns. Two variants, chosen by the
 * payload's `kind`:
 *  - 'mcp': the tool list grouped by consentGroup via ScopeList groupMode; at
 *    least one tool must be selected to approve (INV-MCP-14). Above the list, the
 *    API's fixed `notice` (INV-MCP-25) that the app may handle messages from
 *    other people — rendered verbatim, never hardcoded, no choice attached.
 *  - 'listing-extension': the limited extension consent (BR-28) — what the
 *    extension can and cannot do, from the API's `capabilities`; no tool
 *    choices, approve or deny the whole connection.
 *
 * 403 REAUTHENTICATION_REQUIRED is handled globally by apiFetch (which emits
 * the event that AppRoutes listens to and redirects to /login with returnTo).
 *
 * 404: authorization_id not found / expired — show an inline error.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Info, X } from 'lucide-react';
import { ScopeList } from '@resida/consent-ui';
import type { ConsentScopeItem } from '@resida/consent-ui';
import {
  getMcpConsentRequest,
  submitMcpConsent,
  ApiClientError,
  type ConsentLoadResponse,
  type ConsentToolItem,
} from '../lib/api';

function toolToScopeItem(tool: ConsentToolItem): ConsentScopeItem {
  return {
    name: tool.toolName,
    displayName: tool.displayName,
    description: tool.description,
    category: tool.consentGroup,
    consentGroup: tool.consentGroup,
    mutating: tool.mutating,
  };
}

export function OAuthConsentPage() {
  const [searchParams] = useSearchParams();
  const reqId = searchParams.get('req');

  const [consentData, setConsentData] = useState<ConsentLoadResponse | null>(null);
  const [selectedToolNames, setSelectedToolNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConsent = useCallback(async () => {
    if (!reqId) {
      setError('Missing authorization request ID. Please return to the app you were connecting and start over.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getMcpConsentRequest(reqId);
      setConsentData(data);

      // Pre-check from existingPermittedToolNames; default whoami if empty
      if (data.existingPermittedToolNames.length > 0) {
        setSelectedToolNames(data.existingPermittedToolNames);
      } else {
        const hasWhoami = data.audienceTools.some((t) => t.toolName === 'whoami');
        setSelectedToolNames(hasWhoami ? ['whoami'] : []);
      }
    } catch (err) {
      // The consent kind is unknown until the request loads, so name neither client type.
      if (err instanceof ApiClientError && err.status === 404) {
        setError('This authorization request has expired or was not found. Please return to the app you were connecting and start over.');
      } else {
        setError('Unable to load the authorization request. Please return to the app you were connecting and start over.');
      }
    } finally {
      setLoading(false);
    }
  }, [reqId]);

  useEffect(() => {
    loadConsent();
  }, [loadConsent]);

  function handleToggle(toolName: string, checked: boolean) {
    setSelectedToolNames((prev) =>
      checked ? [...prev, toolName] : prev.filter((n) => n !== toolName)
    );
  }

  const handleApprove = async () => {
    if (!reqId) return;
    try {
      setSubmitting(true);
      setError(null);
      // BR-28: the extension consent approves the whole connection — no tool selection.
      const result = await submitMcpConsent(
        reqId,
        isExtension ? { decision: 'approve' } : { decision: 'approve', grantedToolNames: selectedToolNames }
      );
      window.location.href = result.redirectUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve. Please try again.');
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (!reqId) return;
    try {
      setSubmitting(true);
      setError(null);
      const result = await submitMcpConsent(reqId, { decision: 'deny' });
      window.location.href = result.redirectUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny. Please try again.');
      setSubmitting(false);
    }
  };

  const isExtension = consentData?.kind === 'listing-extension';
  const capabilities = consentData?.capabilities ?? null;
  // INV-MCP-14 applies to the MCP consent only; the extension consent has nothing to select.
  const noToolsSelected = !isExtension && selectedToolNames.length === 0;
  const scopeItems = (consentData?.audienceTools ?? []).map(toolToScopeItem);

  return (
    <div
      data-testid="consent-page"
      className="min-h-screen flex items-center justify-center px-4 py-8 bg-background"
    >
      {loading && (
        <div
          data-testid="consent-page-loading"
          className="text-center"
          role="status"
          aria-live="polite"
          aria-label="Loading authorization request"
        >
          <div
            className="inline-block w-8 h-8 border-4 border-border border-t-primary rounded-full"
            style={{ animation: 'spin 1s linear infinite' }}
            aria-hidden="true"
          />
          <p className="mt-3 text-sm text-muted-foreground">Loading...</p>
        </div>
      )}

      {!loading && error && (
        <div
          role="alert"
          className="max-w-md w-full p-4 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {!loading && !error && consentData && (
        <div
          data-testid="consent-card"
          className="max-w-lg w-full bg-card border border-border rounded-lg p-6 shadow-sm"
        >
          {/* Client identity section */}
          <header className="flex items-center gap-4 mb-4">
            {consentData.client.logoUri ? (
              <img
                data-testid="consent-client-logo"
                src={consentData.client.logoUri}
                alt={`${consentData.client.clientName} logo`}
                width={48}
                height={48}
                className="rounded object-contain"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground flex-shrink-0">
                {consentData.client.clientName.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <h1
                data-testid="consent-client-name"
                className="text-lg font-bold m-0 text-foreground"
              >
                {consentData.client.clientName}
              </h1>
              {consentData.client.clientUri && (
                <a
                  href={consentData.client.clientUri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:underline"
                >
                  {consentData.client.clientUri}
                </a>
              )}
            </div>
          </header>

          {isExtension && capabilities ? (
            <section data-testid="consent-extension-capabilities" aria-labelledby="consent-extension-can-heading">
              <h2
                id="consent-extension-can-heading"
                className="text-base font-semibold mb-2 text-foreground"
              >
                This will let {consentData.client.clientName}:
              </h2>
              <ul data-testid="consent-extension-can-list" className="list-none p-0 m-0 mb-4 space-y-2">
                {capabilities.can.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-success" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <h2 className="text-base font-semibold mb-2 text-foreground">It can't:</h2>
              <ul data-testid="consent-extension-cannot-list" className="list-none p-0 m-0 mb-4 space-y-2">
                {capabilities.cannot.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                    <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p data-testid="consent-preamble" className="text-sm text-muted-foreground mb-3">
                {consentData.preamble}
              </p>
            </section>
          ) : (
            <>
              <h2 className="text-base font-semibold mb-2 text-foreground">
                This app is requesting access to:
              </h2>

              {/* Preamble */}
              <p
                data-testid="consent-preamble"
                className="text-sm text-muted-foreground mb-3"
              >
                {consentData.preamble}
              </p>

              {/* INV-MCP-25: fixed disclosure, server-owned copy — always visible, not dismissible */}
              {consentData.notice && (
                <div
                  role="note"
                  data-testid="consent-notice"
                  className="mb-4 flex items-start gap-2.5 rounded-md border border-info/25 bg-info/10 p-3 text-sm text-foreground"
                >
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-info" aria-hidden="true" />
                  <p className="m-0">{consentData.notice}</p>
                </div>
              )}

              {/* Tool count */}
              <p
                data-testid="consent-tool-count"
                className="text-xs text-muted-foreground mb-3"
              >
                {selectedToolNames.length} of {consentData.audienceTools.length} tool
                {consentData.audienceTools.length !== 1 ? 's' : ''} selected
              </p>

              {/* Scope list grouped by consentGroup */}
              <div data-testid="consent-scope-list" className="mb-4">
                <ScopeList
                  scopes={scopeItems}
                  selectedScopes={selectedToolNames}
                  onToggle={handleToggle}
                  disabled={submitting}
                  groupMode={true}
                />
              </div>

              {/* At-least-one warning (INV-MCP-14) */}
              {noToolsSelected && (
                <p
                  data-testid="consent-at-least-one-warning"
                  role="alert"
                  className="text-sm text-destructive mb-3"
                >
                  Select at least one tool to approve.
                </p>
              )}
            </>
          )}

          {/* Policy links */}
          {(consentData.client.policyUri || consentData.client.tosUri) && (
            <p className="text-xs text-muted-foreground mt-3">
              By approving, you agree to{' '}
              {consentData.client.tosUri && (
                <a
                  data-testid="consent-tos-link"
                  href={consentData.client.tosUri}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
              )}
              {consentData.client.tosUri && consentData.client.policyUri && ' and '}
              {consentData.client.policyUri && (
                <a
                  data-testid="consent-privacy-link"
                  href={consentData.client.policyUri}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              )}
              .
            </p>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 mt-5 justify-end">
            <button
              data-testid="consent-deny-button"
              type="button"
              onClick={handleDeny}
              disabled={submitting}
              aria-label="Deny access"
              className="min-h-[44px] px-4 py-2 text-sm border border-border rounded-md bg-transparent text-foreground hover:bg-muted transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              Deny
            </button>
            <button
              data-testid="consent-approve-button"
              type="button"
              onClick={handleApprove}
              disabled={submitting || noToolsSelected}
              aria-disabled={noToolsSelected}
              aria-label="Approve access"
              className="min-h-[44px] px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Processing…' : 'Approve'}
            </button>
          </div>
        </div>
      )}

      {/* Inline CSS for the spinner animation */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default OAuthConsentPage;
